// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, shorthands, Toast, ToastTitle, useToastController } from '@fluentui/react-components';
import { FC, useEffect, useState } from 'react';
import { NewsWindow } from '../news/NewsWindow';
import { NewsList } from '../news/NewsList';
import { useNews } from '../../libs/hooks/useNews';
import { NewsResponse } from '../../libs/services/NewsService';

const useClasses = makeStyles({
    container: {
        ...shorthands.overflow('hidden'),
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'start',
        height: '100%',
    },
});

export const NewsView: FC = () => {
    const classes = useClasses();
    const news = useNews();
    const { dispatchToast } = useToastController();

    const [selectedNewsId, setSelectedNewsId] = useState<string>('');
    const [newsItems, setNewsItems] = useState<NewsResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const loadMessages = () => {
        setIsLoading(true);

        void news.load()
            .then((loadedNews) => {
                setNewsItems(loadedNews);
            })
            .catch((error) => {
                console.error("Fehler beim Laden der News:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        loadMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReloadNews = () => {
        loadMessages();
        setSelectedNewsId('new');
    };

    const handleDeleteSelected = (ids: string[]) => {
        if (ids.length === 0) return;

        Promise.all(ids.map(id => news.deleteNews(id)))
            .then(() => {
                loadMessages();
                if (ids.includes(selectedNewsId)) {
                    setSelectedNewsId('deleted');
                }
            })
            .catch((_) => {
                dispatchToast(
                    <Toast>
                        <ToastTitle>Beim Löschen ist ein Fehler aufgetreten</ToastTitle>
                    </Toast>,
                    { intent: "error", position: "top-end" }
                );
            });
    };

    const handleSelectNews = (id: string) => {
        setSelectedNewsId(id);
    };

    const handleCreateNewNews = () => {
        setSelectedNewsId('new');
    };

    return (
        <div className={classes.container}>
            <NewsList
                onSelectNews={handleSelectNews}
                onDeleteSelected={handleDeleteSelected}
                selectedNewsId={selectedNewsId}
                newsItems={newsItems}
                isLoading={isLoading}
            />
            <NewsWindow
                newsId={selectedNewsId}
                selectedNews={selectedNewsId && selectedNewsId !== 'new' ?
                    newsItems.find(item => item.id === selectedNewsId) : undefined}
                onReloadNews={handleReloadNews}
                onCreateNews={handleCreateNewNews}
            />
        </div>
    );
};
